import { SelectOption, withForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { isToday, isTomorrow, format, parse, getTime, startOfDay, add } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';

import { defaultSiteLanguage } from '@/globalConsts';

import { sendSurveyFormOptions } from './sendSurveyFormOptions';

export const SendSurveyForm = withForm({
	...sendSurveyFormOptions,
	props: {
		surveySelectOptions: [] as SelectOption[],
		isLoading: false as boolean,
	},
	render: function Render({ form, surveySelectOptions, isLoading }) {
		const isUs = defaultSiteLanguage === 'en-us';
		// US can send from +1 hour, UK can send from +2 hours. This is returned as user's local time
		const startTimeLocal = add(new Date(), { hours: isUs ? 1 : 2 });
		// end date is + 2 years just to minimise size of date picker
		const endDate = add(new Date(), { years: 2 });
		// US has available window of 17.00 - 21:00 UTC. No such window for UK.
		const utcWindowStartHourMin = isUs ? '17:00' : undefined;
		const utcWindowEndHourMin = isUs ? '21:00' : undefined;

		// helper functions
		const formatTimeValue = (dayTime: Date) => {
			// returns time in format HH:MM:SS
			return format(dayTime, 'HH:mm:ss');
		};
		const formatTimeLabel = (dayTime: Date) => {
			// returns time in format hh:MM AM/PM
			return format(dayTime, 'hh:mm aa');
		};
		const getUtcTimestamp = (dayTime: Date) => {
			// converts date to UTC timezone and returns a milliseconds timestamp
			const dateFormat = 'yyyy-MM-dd HH:mm:ss';
			const utcTimeDateStr = formatInTimeZone(dayTime, 'UTC', dateFormat);
			const utcTimeDate = parse(utcTimeDateStr, dateFormat, new Date());
			return getTime(utcTimeDate);
		};
		const getTimestampFromTime = (timeStr: string, baseDate: Date) => {
			// generates a date object merging a time string with a base date
			// returns a milliseconds timestamp to use for comparisons
			const dateFormat = 'HH:mm'; // HH:mm
			// parse uses the base date for any missing date time elements
			const dateTime = parse(timeStr, dateFormat, baseDate);
			return getTime(dateTime);
		};
		const isInRange = (
			dayTime: Date,
			startTimeUtc: number | undefined,
			endTimeUtc: number | undefined
		) => {
			// checks if the given time is within the start / end times (if present)
			let isDisabled = false;
			const thisTimeHourMinUtc = getUtcTimestamp(dayTime);
			if (startTimeUtc) {
				isDisabled = thisTimeHourMinUtc < startTimeUtc;
			}
			if (endTimeUtc && !isDisabled) {
				isDisabled = thisTimeHourMinUtc > endTimeUtc;
			}
			return isDisabled;
		};

		const utcWindowStartDate = utcWindowStartHourMin
			? getTimestampFromTime(utcWindowStartHourMin, startTimeLocal)
			: undefined;

		const utcWindowEndDate = utcWindowEndHourMin
			? getTimestampFromTime(utcWindowEndHourMin, startTimeLocal)
			: undefined;

		const generateTimeOptions = (selectedDateValue: string | undefined) => {
			// returns a label/value array of all times in the day in 30 minute intervals
			// get a timestamp for the window start time
			let startTimeHourMin = utcWindowStartDate;
			if (selectedDateValue) {
				// date value has been selected
				const dateFormat = 'y-MM-dd'; // YYYY-MM-DD
				const selectedDate = parse(selectedDateValue, dateFormat, new Date());
				// if date selected is today or tomorrow we need to do aditional checks to ensure we only show available times
				if (
					isToday(selectedDate) ||
					(isTomorrow(startTimeLocal) && isTomorrow(selectedDate))
				) {
					const localStartTimeUtcHourMinute = getUtcTimestamp(startTimeLocal);
					// if we have a UTC start time window then get the highest value from that or use local start time
					startTimeHourMin = startTimeHourMin
						? Math.max(localStartTimeUtcHourMinute, startTimeHourMin)
						: localStartTimeUtcHourMinute;
				}
			}
			const dayStart = startOfDay(startTimeLocal); // must use startTimeLocal so comparisons work
			let dateTimeValue = dayStart;
			let timeValue = formatTimeValue(dayStart);
			const timeOptions = [] as SelectOption[];
			const endTimeValue = '23:30:00'; // the last 30 minute increment before midnight
			while (timeValue !== endTimeValue) {
				// generate the option label, value and calculate if should be disabled
				timeValue = formatTimeValue(dateTimeValue);
				timeOptions.push({
					label: formatTimeLabel(dateTimeValue),
					value: timeValue,
					disabled: isInRange(dateTimeValue, startTimeHourMin, utcWindowEndDate),
				});
				// add 30 minutes until we reach the end time
				dateTimeValue = add(dateTimeValue, { minutes: 30 });
			}
			return timeOptions;
		};

		const selectedDateValue = useStore(form.store, (state) => state.values.send_date);
		const selectedTime = useStore(form.store, (state) => state.values.send_time);
		const [timeOptions, setTimeOptions] = useState(generateTimeOptions(selectedDateValue));

		useEffect(() => {
			// regenerate the time options based on the newly selected date
			const newTimeOptions = generateTimeOptions(selectedDateValue);
			// check time is still valid if options have changed
			const timeIsFound = newTimeOptions.find(
				(option) => option.value === selectedTime && !option.disabled
			);
			if (!timeIsFound) {
				// selected time is no longer allowed so reset
				form.setFieldValue('send_time', '', { dontUpdateMeta: true, dontValidate: true });
				// set the time field as untouched so that validation errors don't show before user has a chance to interact with the field
				form.setFieldMeta('send_time', (meta) => ({
					...meta,
					isTouched: false,
				}));
			}
			// update the select field with the new time options
			setTimeOptions(newTimeOptions);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [selectedDateValue]);

		const setDateFieldTouched = () => {
			form.setFieldMeta('send_date', (meta) => ({
				...meta,
				isTouched: true,
				isBlurred: true,
			}));
		};

		return (
			<div
				className={`w-full grid grid-cols md:grid-cols-3 gap-4 md:gap-8 mt-6 pb-5 ${isLoading ? 'opacity-0' : ''}`}
			>
				<form.AppField name='survey_version'>
					{(field) => (
						<field.SelectField label='Survey version' options={surveySelectOptions} />
					)}
				</form.AppField>
				<form.AppField name='send_date'>
					{(field) => (
						<field.CustomDateFieldV2
							label='Date to send'
							fromDate={startTimeLocal}
							toDate={endDate}
							setFieldTouched={setDateFieldTouched}
						/>
					)}
				</form.AppField>
				<form.AppField name='send_time'>
					{(field) => (
						<field.SelectField
							label='Time to send'
							options={timeOptions}
							isDisabled={!selectedDateValue}
						/>
					)}
				</form.AppField>
			</div>
		);
	},
});
